import {
  Box,
  Card,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CreateDocResponse,
  HeadingLevel,
  IconPosition,
  MaterialColors,
  SkippedStepTypes,
  CreateCaseResponse,
} from "@/core/types/types";
import DynamicMaterialIcons from "@/core/components/MaterialIcons/MaterialIcons";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { TextVariant } from "@/core/components/Field/Text";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { FieldProps } from "@/core/components/Field";
import { signatureApi } from "@/api/signatureApi";
import { useEffect, useState } from "react";

interface OptionalStepStatusLinkRequiredVariables {
  createCaseResponse?: CreateCaseResponse;
  bookingNumber?: string;
  signatureComponent?: string;
  boardingPass?: string;
  createDocResponse?: CreateDocResponse;
}

function hasNullSignatures(data: CreateDocResponse) {
  const { passengers } = data;
  const checkNullSignature = passengers
    ?.slice(1)
    .some(({ signature }: { signature: Object | null }) => signature === null);
  return !checkNullSignature;
}

async function getPassengersData(createDocResponse: CreateDocResponse) {
  const { passengers, id } = createDocResponse ?? {};
  const hasMultiPassengers: boolean = (passengers?.length ?? 0) > 1;

  if (hasMultiPassengers) {
    const signatureId: string = id;
    const result = await signatureApi.getPassengersDataByDocumentId(
      signatureId
    );
    return result;
  }
  return {};
}

interface PreviousAnswersResult {
  result: OptionalStepStatusLinkRequiredVariables;
}

type GutterlessListProps = OptionalStepStatusLinkRequiredVariables;

const CustomDivider = () => {
  const theme = useTheme();
  return <Divider sx={{ borderColor: theme.palette.secondary.light }} />;
};

const StyledListItemUncompleted = ({ text, type }: any) => {
  const RightArrow = DynamicMaterialIcons["KeyboardArrowRight"];
  const { submitStep } = useClaimCheck();
  return (
    <ListItemButton component="button" sx={{ width: "100%" }}>
      <ListItem
        key={text}
        disableGutters
        onClick={(e) => {
          e.stopPropagation();
          submitStep({
            gotToEnterBookingNumberPage:
              type === SkippedStepTypes.EnterBookingNumber ? "true" : "false",
            goToEnterSignaturePage:
              type === SkippedStepTypes.EnterSignature ? "true" : "false",
            goToUploadBookingConfirmationPage:
              type === SkippedStepTypes.UploadBookingConfirmation
                ? "true"
                : "false",
            goToEnterAdditionalSignaturePage:
              type === SkippedStepTypes.goToAdditionalSignaturePage
                ? "true"
                : "false",
          });
        }}
      >
        <Link color="secondary" underline="hover">
          <TextVariant
            text={text}
            variant={HeadingLevel.Body2}
            iconName="Cancel"
            iconColor={MaterialColors.Error}
            iconPosition={IconPosition.IconBefore}
          />
        </Link>
        <RightArrow />
      </ListItem>
    </ListItemButton>
  );
};

const StyledListItem = (props: {
  text: string;
  checked: boolean;
  type?: SkippedStepTypes;
}) => {
  const { checked, type, text } = props;
  return checked ? (
    <StyledListItemCompleted text={text} />
  ) : (
    <StyledListItemUncompleted text={text} type={type} />
  );
};

const StyledListItemCompleted = ({ text }: { text: string }) => {
  const theme = useTheme();

  return (
    <ListItem key={text} disableGutters sx={{ padding: 2 }}>
      <TextVariant
        text={text}
        variant={HeadingLevel.Body2}
        iconName="CheckCircle"
        iconCustomColor={theme.palette.success.main}
        iconPosition={IconPosition.IconBefore}
      />
    </ListItem>
  );
};

function GutterlessList({
  createCaseResponse,
  bookingNumber,
  signatureComponent,
  boardingPass,
  createDocResponse,
}: Readonly<GutterlessListProps>) {
  const [signatures, setSignatures] = useState(false);
  const [isFirstPassengerSigned, setIsFirstPassengerSigned] = useState(false);
  const shouldShowMultiSignatures: boolean =
    !!createDocResponse?.passengers &&
    (createDocResponse?.passengers?.length ?? 0) > 1;
  const caseOpenedText: string = t(
    `showNextStepsForm.caseOpened.${
      !!createCaseResponse ? "completed" : "missing"
    }`
  );
  const bookingNumberText: string = t(
    `showNextStepsForm.bookingNumber.${
      !!bookingNumber ? "completed" : "missing"
    }`
  );
  const leadCustomerSignatureText: string = t(
    `showNextStepsForm.signature.${
      !!signatureComponent || isFirstPassengerSigned ? "completed" : "missing"
    }`
  );
  const additionalPassengerInformationText: string = t(
    `showNextStepsForm.AdditionalPassengerInformation.${
      !!signatures ? "completed" : "missing"
    }`
  );
  const boardingPassText: string = t(
    `showNextStepsForm.boardingPass.${!!boardingPass ? "completed" : "missing"}`
  );

  useEffect(() => {
    if (createDocResponse) {
      getPassengersData(createDocResponse).then((res: CreateDocResponse) => {
        const isFirstPassengerSignedRespose: boolean =
          !!res?.passengers?.length && res?.passengers[0]?.signature !== null;
        setIsFirstPassengerSigned(isFirstPassengerSignedRespose);
        setSignatures(hasNullSignatures(res));
      });
    }
  }, [createDocResponse, signatures]);

  return (
    <List sx={{ "& li": { justifyContent: "space-between" }, pb: 0 }}>
      <>
        <StyledListItem checked={!!createCaseResponse} text={caseOpenedText} />
        <CustomDivider />
        <StyledListItem
          checked={!!bookingNumber}
          type={SkippedStepTypes.EnterBookingNumber}
          text={bookingNumberText}
        />
        <CustomDivider />
        <StyledListItem
          checked={!!signatureComponent || isFirstPassengerSigned}
          type={SkippedStepTypes.EnterSignature}
          text={leadCustomerSignatureText}
        />
        {shouldShowMultiSignatures && (
          <>
            <CustomDivider />
            <StyledListItem
              checked={signatures}
              type={SkippedStepTypes.goToAdditionalSignaturePage}
              text={additionalPassengerInformationText}
            />
          </>
        )}
        <CustomDivider />
        <StyledListItem
          checked={!!boardingPass}
          type={SkippedStepTypes.UploadBookingConfirmation}
          text={boardingPassText}
        />
      </>
    </List>
  );
}

const OptionalStepStatusLink = ({ field }: FieldProps) => {
  const {
    properties: { requiredInputVariables },
  } = field;

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result }: PreviousAnswersResult = useGetPreviousAnswers(
    requiredInputVariables
  );

  const {
    createCaseResponse,
    bookingNumber,
    signatureComponent,
    boardingPass,
    createDocResponse,
  } = result;
  return (
    <Card sx={{ boxShadow: "none" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        m={2}
      >
        <Typography variant={HeadingLevel.Body2}>
          {t("showNextStepsForm.title")}
        </Typography>
        <Typography variant={HeadingLevel.Subtitle1}>
          {createCaseResponse?.legalCasePublicId || "N/A"}
        </Typography>
      </Box>
      <CustomDivider />
      <GutterlessList
        createCaseResponse={createCaseResponse}
        bookingNumber={bookingNumber}
        signatureComponent={signatureComponent}
        boardingPass={boardingPass}
        createDocResponse={createDocResponse}
      />
    </Card>
  );
};

export default OptionalStepStatusLink;
